import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const teachers = [
  {
    name: '教师A',
    subtitle: '案例驱动型',
    score: 72,
    features: [
      { label: '案例使用', value: '情境案例 + 时事素材', strength: 90 },
      { label: '问题设计', value: '开放性提问链', strength: 85 },
      { label: '互动方式', value: '小组讨论 + 全班分享', strength: 60 },
      { label: '理论阐释', value: '案例→理论单向连接', strength: 45 },
      { label: '资源支持', value: '多媒体丰富', strength: 80 },
    ],
    color: 'primary' as const,
    icon: 'ri-lightbulb-flash-line',
  },
  {
    name: '教师B',
    subtitle: '综合优秀型',
    score: 94,
    features: [
      { label: '案例使用', value: '经典文本案例', strength: 80 },
      { label: '问题设计', value: '递进追问结构', strength: 95 },
      { label: '互动方式', value: '苏格拉底式对话', strength: 90 },
      { label: '理论阐释', value: '严密的逻辑推演', strength: 92 },
      { label: '资源支持', value: '文献推荐丰富', strength: 88 },
    ],
    color: 'accent' as const,
    icon: 'ri-vip-crown-line',
  },
  {
    name: '教师C',
    subtitle: '基础待提升型',
    score: 48,
    features: [
      { label: '案例使用', value: '生活化案例，缺乏理论桥接', strength: 50 },
      { label: '问题设计', value: '基础问答为主', strength: 40 },
      { label: '互动方式', value: '学生参与积极但深度不足', strength: 55 },
      { label: '理论阐释', value: '理论讲解清晰但独立', strength: 48 },
      { label: '资源支持', value: '需要补充权威资料', strength: 45 },
    ],
    color: 'secondary' as const,
    icon: 'ri-user-search-line',
  },
];

const colorMap = {
  primary: {
    bg: 'bg-primary-500',
    bgLight: 'bg-primary-100',
    bgMedium: 'bg-primary-200',
    text: 'text-primary-600',
    textDark: 'text-primary-700',
    border: 'border-primary-300/50',
    ring: 'ring-primary-200',
    bar: 'from-primary-400 to-primary-600',
    barBg: 'bg-primary-100',
    dot: 'bg-primary-500',
  },
  accent: {
    bg: 'bg-accent-500',
    bgLight: 'bg-accent-100',
    bgMedium: 'bg-accent-200',
    text: 'text-accent-600',
    textDark: 'text-accent-700',
    border: 'border-accent-300/50',
    ring: 'ring-accent-200',
    bar: 'from-accent-400 to-accent-600',
    barBg: 'bg-accent-100',
    dot: 'bg-accent-500',
  },
  secondary: {
    bg: 'bg-secondary-500',
    bgLight: 'bg-secondary-100',
    bgMedium: 'bg-secondary-200',
    text: 'text-secondary-600',
    textDark: 'text-secondary-700',
    border: 'border-secondary-300/50',
    ring: 'ring-secondary-200',
    bar: 'from-secondary-400 to-secondary-600',
    barBg: 'bg-secondary-100',
    dot: 'bg-secondary-500',
  },
};

export default function P71TeachingGroup({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-flex items-center gap-2 text-base font-body font-semibold text-accent-500 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一教研组一图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从个体经验走向<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">共同教研</span>
          </h2>
          <p className={`text-base text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            教研组不只看谁讲得好，更要看好在哪里、问题在哪里、经验怎样共享
          </p>
        </div>

        {/* Teacher Comparison Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-6xl mx-auto mb-3 items-start transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {teachers.map((t, ti) => {
            const c = colorMap[t.color];
            const isCenter = ti === 1;
            return (
              <div
                key={t.name}
                className={`relative rounded-2xl overflow-hidden transition-all duration-500 glass-liquid ${isCenter ? 'md:-translate-y-2 ring-2 ring-accent-200/60' : ''} ${c.border}`}
                style={{ transitionDelay: `${300 + ti * 150}ms` }}
              >
                {/* Top color bar */}
                <div className={`h-1.5 w-full ${c.bg}`} />

                {/* Header */}
                <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-xl ${c.bgLight} border ${c.border} flex items-center justify-center`}>
                      <i className={`${t.icon} text-xl ${c.text}`} />
                    </div>
                    <div>
                      <div className="font-heading text-base font-bold text-foreground-950">{t.name}</div>
                      <div className={`text-xs font-body font-medium ${c.textDark}`}>{t.subtitle}</div>
                    </div>
                  </div>
                  <div className={`w-12 h-12 rounded-full ${c.bgLight} border ${c.border} flex flex-col items-center justify-center`}>
                    <span className={`number-font text-base font-bold ${c.text}`}>{t.score}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="mx-4 h-px bg-background-200/80" />

                {/* Feature Bars */}
                <div className="px-4 py-3 space-y-3">
                  {t.features.map((f, fi) => (
                    <div key={f.label} style={{ transitionDelay: `${500 + ti * 150 + fi * 60}ms` }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-body font-semibold text-foreground-950">{f.label}</span>
                        <span className={`text-xs font-body ${f.strength >= 80 ? c.text : f.strength >= 60 ? 'text-foreground-950' : 'text-foreground-950'}`}>
                          {f.value}
                        </span>
                      </div>
                      <div className={`h-2 rounded-full ${c.barBg} overflow-hidden`}>
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${c.bar} transition-all duration-1000 ease-out`}
                          style={{
                            width: titleVisible ? `${f.strength}%` : '0%',
                            transitionDelay: `${600 + ti * 100 + fi * 80}ms`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Convergence Arrow + Shareable Section */}
        <div className={`max-w-4xl mx-auto mb-3 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Three arrows pointing down */}
          <div className="flex justify-center mb-2">
            <div className="flex gap-[22%] md:gap-[26%]">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-px h-4 bg-gradient-to-b from-transparent to-accent-300/60" />
                  <i className="ri-arrow-down-s-line text-sm text-accent-400/70 -mt-1" />
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-2xl glass-liquid-gold">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-accent-100 border border-accent-200/50 flex items-center justify-center">
                <i className="ri-share-forward-line text-base text-accent-600" />
              </div>
              <span className="text-base font-heading font-bold text-accent-700">可共享经验</span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {[
                { label: '优秀案例', desc: '情境+时事素材设计法' },
                { label: '追问方法', desc: '递进式提问链模板' },
                { label: '理论结构', desc: '案例→理论双向桥接' },
              ].map((item, i) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-background-50/80 border border-accent-200/30 shadow-sm"
                  style={{ transitionDelay: `${600 + i * 80}ms` }}
                >
                  <span className="w-2 h-2 rounded-full bg-accent-400 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-body font-bold text-accent-800">{item.label}</span>
                    <span className="text-xs font-body text-accent-600 ml-1.5">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Summary */}
        <div className={`max-w-3xl mx-auto transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 rounded-xl glass-liquid">
            <div className="flex items-start gap-3">
              <div className="w-1 h-full min-h-[3rem] rounded-full bg-gradient-to-b from-accent-300/50 to-primary-300/30 flex-shrink-0 mt-0.5" />
              <p className="text-base text-foreground-950 font-body leading-relaxed">
                AI的作用，是把分散在不同课堂中的个体经验和共同问题组织为<strong className="text-accent-600">可共享、可讨论、可迁移</strong>的教研资源。共性短板如导入过长、核心问题出现较晚、案例多但理论回扣不足，可以转化为集体备课的明确议题。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}